import { useState, useRef } from 'react'
import { router } from '@inertiajs/react'
import {
  ChevronLeft,
  ChevronDown,
  Upload,
  FileSpreadsheet,
  AlertCircle,
} from 'lucide-react'

interface Stream {
  id: string
  name: string
}

interface Props {
  streams: Stream[]
  preselectedStreamId: string | null
}

export default function ItemsUpload({ streams, preselectedStreamId }: Props) {
  const [selectedStreamId, setSelectedStreamId] = useState<string>(
    preselectedStreamId || ''
  )
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    setError(null)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
      } else {
        setError('Please upload a CSV file')
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile)
      } else {
        setError('Please upload a CSV file')
      }
    }
  }

  const handleUpload = async () => {
    if (!file || !selectedStreamId) return

    setIsUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('stream_id', selectedStreamId)

      // Get CSRF token from meta tag
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      const response = await fetch('/items/upload_csv', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
        body: formData,
      })

      if (response.ok || response.redirected) {
        // Follow the redirect
        router.visit('/items')
      } else {
        const text = await response.text()
        setError(text || 'Upload failed. Please try again.')
        setIsUploading(false)
      }
    } catch (err) {
      setError('Upload failed. Please try again.')
      setIsUploading(false)
    }
  }

  const handleBack = () => {
    router.visit('/items')
  }

  const selectedStream = streams.find((s) => s.id === selectedStreamId)

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-2xl mx-auto px-6 py-5">
          {/* Breadcrumb */}
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Items</span>
          </button>

          <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
            Upload CSV
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
            Import items from a CSV file into a stream.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 space-y-6">
          {/* Stream Selection */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              Select Stream <span className="text-red-500">*</span>
            </label>
            {streams.length === 0 ? (
              <div className="px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-sm">
                No streams available. Please create a stream first.
              </div>
            ) : (
              <div className="relative">
                <select
                  value={selectedStreamId}
                  onChange={(e) => setSelectedStreamId(e.target.value)}
                  className="w-full appearance-none px-4 py-3 pr-10 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                >
                  <option value="">Choose a stream...</option>
                  {streams.map((stream) => (
                    <option key={stream.id} value={stream.id}>
                      {stream.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 dark:text-stone-500 pointer-events-none" />
              </div>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              CSV File <span className="text-red-500">*</span>
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                ${isDragging
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30'
                  : file
                    ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30'
                    : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600 hover:bg-stone-50 dark:hover:bg-stone-800/50'
                }
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelect}
                className="hidden"
              />

              {file ? (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mb-3">
                    <FileSpreadsheet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <p className="font-medium text-stone-900 dark:text-stone-100">
                    {file.name}
                  </p>
                  <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(null)
                    }}
                    className="mt-3 text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                  >
                    Choose different file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-stone-100 dark:bg-stone-800 flex items-center justify-center mb-3">
                    <Upload className="w-6 h-6 text-stone-400 dark:text-stone-500" />
                  </div>
                  <p className="font-medium text-stone-900 dark:text-stone-100">
                    Drop your CSV file here
                  </p>
                  <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                    or click to browse
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Help text */}
          <div className="rounded-lg bg-stone-50 dark:bg-stone-800/50 px-4 py-4 space-y-3">
            <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
              CSV Column Mapping
            </p>
            <p className="text-sm text-stone-600 dark:text-stone-400">
              Your file should include a header row. We automatically recognize these column names:
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-stone-500 dark:text-stone-400 w-16 flex-shrink-0">Sender:</span>
                <div className="flex flex-wrap gap-1">
                  {['from', 'sender', 'author', 'email'].map((col) => (
                    <code key={col} className="px-1.5 py-0.5 bg-stone-200 dark:bg-stone-700 rounded text-xs text-stone-700 dark:text-stone-300">{col}</code>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-stone-500 dark:text-stone-400 w-16 flex-shrink-0">Subject:</span>
                <div className="flex flex-wrap gap-1">
                  {['subject', 'title', 'heading', 'name'].map((col) => (
                    <code key={col} className="px-1.5 py-0.5 bg-stone-200 dark:bg-stone-700 rounded text-xs text-stone-700 dark:text-stone-300">{col}</code>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-stone-500 dark:text-stone-400 w-16 flex-shrink-0">Content:</span>
                <div className="flex flex-wrap gap-1">
                  {['content', 'body', 'message', 'text', 'description'].map((col) => (
                    <code key={col} className="px-1.5 py-0.5 bg-stone-200 dark:bg-stone-700 rounded text-xs text-stone-700 dark:text-stone-300">{col}</code>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-400 pt-1">
              If no matching headers are found, columns are mapped by position: 1st column = sender, 2nd = subject, 3rd = content.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={handleBack}
              disabled={isUploading}
              className="px-4 py-2.5 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || !selectedStreamId || isUploading || streams.length === 0}
              className="px-6 py-2.5 text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
